import {
  CAMPAIGN_FOLLOW_UP_ACTION_NAME_SEND_EMAIL,
  CAMPAIGN_FOLLOW_UP_ACTION_NAME_SEND_SMS,
  CAMPAIGN_FOLLOW_UP_ACTION_NAME_SEND_WHATSAPP,
  type CampaignFollowUpActionTypeRef
} from '@/interfaces/Campaign.interface';

export const configuratorBaseUrl = () =>
  import.meta.env.VITE_CONFIGURATOR_URL?.replace(/\/$/, '') ?? '';

export { formatStageWorkflowCount } from './pipelineStageWorkflows';

export function getConfiguratorCrmProductWorkflowsUrl(productId: string): string {
  const base = configuratorBaseUrl();
  const id = productId.trim();
  if (!base || !id) return '';
  return `${base}/dashboard/products/edit/${encodeURIComponent(id)}/workflows?branch=Draft&newDealWorkflow=1`;
}

export function getConfiguratorCampaignEditUrl(uuid: string): string {
  return `${configuratorBaseUrl()}/campaigns/${uuid}`;
}

export function getConfiguratorPolicyManagementUrl(): string {
  return `${configuratorBaseUrl()}/policy-management/policies`;
}

export type TemplateChannel = 'email' | 'sms' | 'whatsapp';

/** PBv2 canonical campaign type names (Email/SMS/WhatsApp) mapped to template channels. */
const CAMPAIGN_TYPE_CHANNELS: Record<string, TemplateChannel> = {
  Email: 'email',
  SMS: 'sms',
  WhatsApp: 'whatsapp'
};

export function getCampaignTypeChannel(
  campaignTypeName?: string | null
): TemplateChannel | null {
  if (!campaignTypeName) {
    return null;
  }
  return CAMPAIGN_TYPE_CHANNELS[campaignTypeName] ?? null;
}

const FOLLOW_UP_ACTION_CHANNELS: Record<string, TemplateChannel> = {
  [CAMPAIGN_FOLLOW_UP_ACTION_NAME_SEND_EMAIL]: 'email',
  [CAMPAIGN_FOLLOW_UP_ACTION_NAME_SEND_SMS]: 'sms',
  [CAMPAIGN_FOLLOW_UP_ACTION_NAME_SEND_WHATSAPP]: 'whatsapp'
};

export function getFollowUpActionChannel(
  actionType?: CampaignFollowUpActionTypeRef | null
): TemplateChannel | null {
  if (!actionType?.name) {
    return null;
  }
  return FOLLOW_UP_ACTION_CHANNELS[actionType.name] ?? null;
}

export function getConfiguratorTemplateEmbedUrl(
  productId: string,
  templateId: string,
  channel: TemplateChannel
): string {
  const base = configuratorBaseUrl();
  const params = new URLSearchParams({ channel });
  return `${base}/embed/products/${encodeURIComponent(
    productId
  )}/templates/${encodeURIComponent(templateId)}?${params.toString()}`;
}

/** Embed URL rendering a scaled-down, non-scrollable preview for card grids. */
export function getConfiguratorTemplateThumbnailUrl(
  productId: string,
  templateId: string,
  channel: TemplateChannel
): string {
  const base = configuratorBaseUrl();
  const params = new URLSearchParams({ channel, mode: 'thumbnail' });
  return `${base}/embed/products/${encodeURIComponent(
    productId
  )}/templates/${encodeURIComponent(templateId)}?${params.toString()}`;
}

/** PBv2 dashboard segments for managing each template channel. */
const TEMPLATE_MANAGE_SEGMENTS: Record<TemplateChannel, string> = {
  email: 'emails',
  sms: 'sms',
  whatsapp: 'whatsapp-messages'
};

export function getConfiguratorTemplateManageUrl(
  productId: string,
  channel: TemplateChannel
): string {
  return `${configuratorBaseUrl()}/dashboard/products/edit/${encodeURIComponent(
    productId
  )}/${TEMPLATE_MANAGE_SEGMENTS[channel]}`;
}

/** PBv2 editor URL for a single message (template) within a channel. */
export function getConfiguratorTemplateEditUrl(
  productId: string,
  templateId: string,
  channel: TemplateChannel
): string {
  return `${getConfiguratorTemplateManageUrl(
    productId,
    channel
  )}/edit/${encodeURIComponent(templateId)}`;
}
